"use client";

import { ChevronRight, Crown, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useAds } from "@/components/ads/Ads";
import Link from "@/components/ui/Link";
import { useUi } from "@/components/ui/use-ui";
import { formatUsd } from "@/lib/billing/pricing";
import { splitLocale } from "@/lib/i18n";
import { fmt } from "@/lib/locales/fmt";
import { track } from "@/lib/stats-client";
import { SweetCoin } from "./SweetCoin";

// Now and then a Sweet Coin floats in at the side of the page, offering Premium and packs of coins: to
// visitors without Premium who have been on the site a while, at most once every few days, never on the
// pages that sell them or on the sign-in pages. A tap opens the offer in the middle of the screen; closing
// it puts it away for days.

const SEEN_KEY = "sweet.offer";
/** The age question's answer (AGE_STORAGE_KEY in components/shell/AgeGate.tsx, a server module). */
const AGE_KEY = "sweet.age";
/** Days between offers. */
const EVERY_DAYS = 3;
/** Seconds on the site (this visit) before the coin comes. */
const AFTER_SECONDS = 90;
/** Where it never comes. */
const QUIET = ["/subscription", "/login", "/signup", "/account", "/forgot-password", "/reset-password", "/takedown"];

const store = {
  get(key: string) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch {}
  },
};

/** The cheapest membership, the most coins one gives, the cheapest pack. */
export interface OfferPrices {
  premiumFrom: number | null;
  premiumCoins: number;
  packsFrom: number | null;
}

export function CoinOffer({ prices }: { prices: OfferPrices }) {
  const { adFree } = useAds();
  const t = useUi();
  // Without the language, so /de/subscription and /ja/login stay quiet too.
  const path = splitLocale(usePathname() ?? "/").path;
  const quiet = QUIET.some((p) => path === p || path.startsWith(`${p}/`));
  const [stage, setStage] = useState<"hidden" | "coin" | "open">("hidden");

  // Its time comes once per visit, if the last offer was days ago.
  useEffect(() => {
    if (adFree !== false || quiet || stage !== "hidden" || (!prices.premiumFrom && !prices.packsFrom)) return;
    const last = Number(store.get(SEEN_KEY)) || 0;
    if (Date.now() - last < EVERY_DAYS * 86_400_000) return;
    const timer = window.setTimeout(() => {
      // Not over the age question, a video in full screen, or a hidden tab.
      if (store.get(AGE_KEY) !== "ok" || document.fullscreenElement || document.visibilityState !== "visible") return;
      store.set(SEEN_KEY, String(Date.now()));
      track("offer", "shown");
      setStage("coin");
    }, AFTER_SECONDS * 1000);
    return () => window.clearTimeout(timer);
  }, [adFree, quiet, stage, prices.premiumFrom, prices.packsFrom]);

  if (stage === "hidden" || quiet || adFree !== false) return null;

  const close = () => {
    track("offer", "close");
    setStage("hidden");
  };

  return (
    <>
      {stage === "open" && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4" role="dialog" aria-modal="true" aria-label={t.pay.offerLabel}>
          <div className="absolute inset-0 bg-black/70" onClick={() => setStage("coin")} aria-hidden />
          <div className="glass-strong relative w-full max-w-[20rem] animate-[pwa-rise_.25s_ease-out] overflow-hidden rounded-3xl p-5 shadow-[0_30px_90px_-25px_rgb(255_46_126/0.6)]">
            <span aria-hidden className="pointer-events-none absolute -top-16 left-1/2 h-40 w-64 -translate-x-1/2 rounded-full bg-brand/30 blur-3xl" />
            <button type="button" onClick={close} aria-label={t.common.close} className="absolute right-3 top-3 grid size-8 place-items-center rounded-full text-muted hover:bg-white/10 hover:text-fg">
              <X size={16} />
            </button>
            <SweetCoin size={80} className="relative mx-auto animate-[offer-bob_2.4s_ease-in-out_infinite] drop-shadow-[0_12px_30px_rgb(255_70_150/0.55)]" />
            <p className="relative mt-2 text-center text-[20px] font-semibold text-fg">{t.pay.offerTitle}</p>
            <p className="relative text-center text-[13px] text-muted">{t.pay.offerFor}</p>
            <div className="relative mt-4 space-y-2">
              {prices.premiumFrom != null && (
                <Link
                  href="/subscription"
                  onClick={() => track("offer", "premium")}
                  className="flex items-center gap-3 rounded-2xl bg-linear-to-r from-[#ffe58f] to-[#e0a12c] px-3.5 py-3 text-[#1b1206] transition-[filter] hover:brightness-110"
                >
                  <Crown size={20} className="shrink-0" />
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block text-[14.5px] font-semibold">{fmt(t.pay.premiumFrom, { price: formatUsd(prices.premiumFrom) })}</span>
                    <span className="block text-[12px] opacity-80">{fmt(t.pay.upTo, { n: prices.premiumCoins.toLocaleString("en-US") })}</span>
                  </span>
                  <ChevronRight size={18} className="shrink-0" />
                </Link>
              )}
              {prices.packsFrom != null && (
                <Link href="/subscription#coins" onClick={() => track("offer", "coins")} className="flex items-center gap-3 rounded-2xl bg-white/[0.07] px-3.5 py-3 text-fg transition-colors hover:bg-white/[0.12]">
                  <SweetCoin size={22} />
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block text-[14.5px] font-medium">{fmt(t.pay.packsFrom, { price: formatUsd(prices.packsFrom) })}</span>
                    <span className="block text-[12px] text-muted">{t.pay.collectFree}</span>
                  </span>
                  <ChevronRight size={18} className="shrink-0 text-muted" />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
      {/* The floating coin, at the side; a tap opens the offer in the middle of the screen. */}
      <div className="pointer-events-none fixed bottom-[calc(env(safe-area-inset-bottom)+10.5rem)] right-3 z-[45] md:bottom-44 md:right-6">
        <div className="pointer-events-auto relative">
          <button
            type="button"
            aria-label={t.pay.offerButton}
            onClick={() => {
              if (stage === "coin") track("offer", "open");
              setStage("open");
            }}
            className="relative grid size-16 animate-[offer-in_.6s_cubic-bezier(.2,1.4,.4,1)] place-items-center rounded-full"
          >
            <span aria-hidden className="absolute inset-1 animate-ping rounded-full bg-brand/30" />
            <SweetCoin size={64} className="relative animate-[offer-bob_2.4s_ease-in-out_infinite] drop-shadow-[0_10px_24px_rgb(255_70_150/0.6)]" />
          </button>
          <button type="button" onClick={close} aria-label={t.common.close} className="absolute -left-1 -top-1 grid size-6 place-items-center rounded-full bg-black/70 text-white/80 ring-1 ring-white/15 hover:text-white">
            <X size={13} />
          </button>
        </div>
      </div>
    </>
  );
}
